import React, {useState} from 'react';
import styled from 'styled-components';

import {useModalContext} from '@bootcamp/web/src/contexts/Modal';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {H4, Body3} from '@bootcamp/web/src/components/Typography/next';
import {Container, Chiclet, Header, Wrapper, HeaderText, BackIcon, CloseIcon, ButtonArea, Panel} from './shared';
import {ArrowUpRight} from '@styled-icons/fluentui-system-filled/ArrowUpRight';
import {DocumentArrowDown} from '@styled-icons/fluentui-system-filled/DocumentArrowDown';
import {ArrowCounterclockwise} from '@styled-icons/fluentui-system-filled/ArrowCounterclockwise';

import {RoundButton} from '@bootcamp/web/src/components/UI';
import {AssignmentWithoutContext as Assignment, typeDetails, CalendarDayWithoutContext} from '../CalendarDay';

import {InputComponent, CustomInputWrapper} from '@bootcamp/web/src/components/UI/inputs';

import theme from '@bootcamp/shared/src/styles/theme';
import {subjectDetails} from '../CalendarDay';

const SettingsContainer = styled(Container)`
  max-width: 491px;
  font-family: 'proxima-nova';
  min-height: 0px;
  height: calc(100% - 64px);
  ${({theme}) => theme.mediaQueries.tablet} {
    max-height: 100%;
    max-width: 100%;
    height: 100%;
    width: 100%;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
    top: unset;
    overflow: hidden;
  }

`;
const ResetSettingsContainer = styled(SettingsContainer)`
  min-height: 475px;
  max-height: 736px;
  height: auto;
`;
const SettingsWrapper = styled(Wrapper)`
  flex-direction: column;
  overflow-y: scroll;
`;

const SettingsSection = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid ${({theme}) => theme.colors.neutralsPalette.light};

  &:last-child {
    border-bottom: none;
  }
`;
const StyledPanel = styled(Panel)`
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
`;

const SettingsOptions = styled.div`
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 8px;
  margin-top: 16px;
  ${({theme}) => theme.mediaQueries.tablet} {
    flex-direction: ${({mobileVertical}) => mobileVertical ? 'column' : 'row'};
  }
`;

const SettingsOption = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  padding: 13px 16px;
  border-radius: 8px;
  ${({selected, theme}) => selected ? `
    font-weight: 700;
    border: 1px solid ${theme.colors.brandPalette.royal.default};
    background: ${theme.colors.brandPalette.royal.extraLight}40;
  ` : `
    font-weight: 400;
    border: 1px solid ${theme.colors.neutralsPalette.light};
  `}
  ${({theme}) => theme.mediaQueries.tablet} {
    width: 100%;
    justify-content: center;
  }
`;
const AutoWidthSettingsOption = styled(SettingsOption)`
  width: 50%;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  div {
    width: 100%;
    max-width: 100%;
  }
`;
const SettingsOptionHeader = styled.span`
  margin-bottom: 12px;
  text-align: center;
  width: 100%;
  margin-top: 3px;
`;
const SettingsSectionHeader = styled(H4)`
  margin-bottom: 8px;
`;
const SettingsSectionSubheader = styled(Body3)`
  margin-bottom: 8px;
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
`;
const ResetLabel = styled(SettingsSectionSubheader)`
  margin: 0;
`;

const ResetScheduleButton = styled(RoundButton)`
`;

const ConfirmResetSection = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const ResetButtonArea = styled(ButtonArea)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
`;
const StudyGuideLink = styled.a`
  display: flex;
  height: 42px;
  padding: 12px 16px;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  outline: none;
  text-decoration: none;
  color: ${({theme}) => theme.colors.neutralsPalette.black};
  margin-bottom: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'proxima-nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 100%; /* 16px */
`;
const LinkIcon = styled(ArrowUpRight)`
  width: 20px;
  height: 20px;
  color: ${({theme}) => theme.colors.borderPalette.secondary};
  margin-left: auto;
`;
const StyledDownloadButton = styled(RoundButton)`
  margin-bottom: 16px;
`;
const DocumentIcon = styled(DocumentArrowDown)`
  width: 20px;
  height: 20px;
`;
const ResetIcon = styled(ArrowCounterclockwise)`
  width: 20px;
  height: 20px;
`;

const AssignmentTypeWrapper = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 4px;
`;
const Alert = styled.span`
  font-weight: 700;
  color: ${({theme}) => theme.colors.interfacePalette.red.default};
`;
const Checkbox = styled(InputComponent)`
  align-items: flex-start;

  ${CustomInputWrapper} {
    margin-right: 8px;
    margin-top: 4px;
  }
`;

const StyledButtonArea = styled(ButtonArea)`
  padding: 16px 24px;
  border-top: 1px solid ${({theme}) => theme.colors.neutralsPalette.light};
  background: white;
  border-bottom-right-radius: 8px;
  border-bottom-left-radius: 8px;

  ${({theme}) => theme.mediaQueries.tablet} {
    border-radius: 0px;
  }
`;
const SettingsModal = ({close, resetSchedule, eventsByDate}) => {
  const [resetScreenVisible, setResetScreenVisible] = useState(false);
  const [resetConfirmed, setResetConfirmed] = useState(false);
  const {searchUserInteractions, saveUserInteraction, bootcamp} = useUserDataContext();
  const {modalDispatch} = useModalContext();

  const defaultStartWeekOn = searchUserInteractions('startWeekOn', 0);
  const defaultShowTimeInAssignment = searchUserInteractions('showTimeInAssignment', 1);

  const [startWeekOn, setStartWeekOn] = useState(defaultStartWeekOn);
  const [showTimeInAssignment, setShowTimeInAssignment] = useState(defaultShowTimeInAssignment);

  const hideCompletedAssignments = searchUserInteractions('hideCompletedAssignments', false);
  const useSubjectColors = searchUserInteractions('useSubjectColors', false) && ['dat', 'inbde', 'oat', 'med-school'].includes(bootcamp);
  const studySpeed = searchUserInteractions('studySpeed', 1);

  // should only show subject color coding settings if dat bootcamp & first day is after 2025-03-01
  const firstDay = Object.entries(eventsByDate).sort((a, b) => new Date(a?.[0]) - new Date(b?.[0])).filter(([date, value]) => value.length > 0)?.[0]?.[0];
  const shouldShowSubjectCoding = ['dat', 'inbde', 'oat', 'med-school'].includes(bootcamp) && new Date(firstDay) > new Date('2025-03-01');

  function handleStartWeekOn(value) {
    setStartWeekOn(value);
    saveUserInteraction('startWeekOn', value);
  }

  function handleShowTimeInAssignment(value) {
    setShowTimeInAssignment(value);
    saveUserInteraction('showTimeInAssignment', value);
  }
  function handleHideCompletedAssignments(value) {
    saveUserInteraction('hideCompletedAssignments', value);
  }
  function handleUseSubjectColors(value) {
    saveUserInteraction('useSubjectColors', value);
  }

  function handleBackClick() {
    setResetScreenVisible(false);
    setResetConfirmed(false);
  }
  const assignmentTypeTitles = bootcamp === 'dat' ? [
    ['qbank', 'Qbanks'],
    ['video', 'Videos'],
    ['bites', 'Bites'],
    ['resource', 'Resources'],
    ['practiceTest', 'Practice Tests'],
  ] : [
    ['qbank', 'Qbanks'],
    ['video', 'Videos'],
    ['createTest', 'Create Test'],
    ['resource', 'Resources'],
    ['practiceTest', 'Readiness Exams'],
  ];
  const subjectTypeTitles = Object.keys(subjectDetails[bootcamp] || {});
  const assignmentPropsShim = bootcamp === 'med-school' ?
    {
      event: {
        title: 'Vascular System Videos 1-7',
        type: 'video',
        subject: 'Cardiovascular System',
        estimatedTime: 75,
        disabled: false,
      },
      provided: {draggableProps: {}, dragHandleProps: {}},
      updateCompleteStatus: () => { },
      showEstimatedTime: true,
    }
  : bootcamp === 'inbde' ? {
    event: {
      title: 'Operative Dentistry Qbank (First Half)',
      type: 'qbank',
      subject: 'Fields of Dentistry',
      estimatedTime: 75,
      disabled: false,
    },
    provided: {draggableProps: {}, dragHandleProps: {}},
    updateCompleteStatus: () => { },
    showEstimatedTime: true,
  } : bootcamp === 'dat' ? {
    event: {
      title: 'Equations and Inequalities Qbank',
      type: 'qbank',
      subject: 'Quantitative Reasoning',
      estimatedTime: 60,
      disabled: false,
    },
    provided: {draggableProps: {}, dragHandleProps: {}},
    updateCompleteStatus: () => { },
    showEstimatedTime: true,
  } : {
    event: {
      title: '35 Adult Health Standalone Questions',
      type: 'qbank',
      estimatedTime: 75,
      disabled: true,
    },
    provided: {draggableProps: {}, dragHandleProps: {}},
    updateCompleteStatus: () => { },
    showEstimatedTime: true,
  };


  if (resetScreenVisible) {
    return (
      <ResetSettingsContainer onClick={e => e.stopPropagation()}>
        <SettingsWrapper style={{overflow: 'auto'}}>
          <Header>
            <BackIcon onClick={handleBackClick} />
            <HeaderText>Settings</HeaderText>
            <CloseIcon onClick={() => modalDispatch({type: 'close'})}/>
          </Header>
          <StyledPanel>
            <SettingsSectionHeader>
              Reset Schedule
            </SettingsSectionHeader>
            <SettingsSectionSubheader>
              <Alert>Resetting your schedule will clear all completed assignments and mark them as incomplete.</Alert> To keep your current progress, rebalance your schedule instead.
            </SettingsSectionSubheader>
            <ConfirmResetSection>
              <Checkbox
                checked={resetConfirmed}
                onClick={() => setResetConfirmed(!resetConfirmed)}
                color={theme.colors.brandPalette.blue.default}>
                <ResetLabel>
                    I understand that after resetting I will lose my progress and my current schedule can’t be recovered.
                </ResetLabel>
              </Checkbox>
            </ConfirmResetSection>
            <ResetButtonArea>
              <RoundButton full small onClick={() => {
                setResetConfirmed(false);
                setResetScreenVisible(false);
              }}>
                Cancel
              </RoundButton>
              <RoundButton full small onClick={() => {
                resetSchedule();
                close();
              }} disabled={!resetConfirmed} themePalette={{...theme.colors.interfacePalette.red, default: '#EF4444'}}>
                Reset Schedule
              </RoundButton>
            </ResetButtonArea>
          </StyledPanel>
        </SettingsWrapper>
      </ResetSettingsContainer>
    )
  }

  return (
    <SettingsContainer onClick={e => e.stopPropagation()}>
      <Header>
        <HeaderText>Settings</HeaderText>
        <CloseIcon onClick={() => modalDispatch({type: 'close'})} />
      </Header>
      <SettingsWrapper>
        <Panel>
          <SettingsSection>
            <SettingsSectionHeader>Start week on</SettingsSectionHeader>
            <SettingsOptions>
              <SettingsOption selected={startWeekOn === 0} onClick={() => handleStartWeekOn(0)}>Sunday</SettingsOption>
              <SettingsOption selected={startWeekOn === 1} onClick={() => handleStartWeekOn(1)}>Monday</SettingsOption>
            </SettingsOptions>
          </SettingsSection>
          {/* <SettingsSection>
            <SettingsSectionHeader>Sort completed assignments</SettingsSectionHeader>
            <SettingsOptions>
              <SettingsOption selected={sortCompletedAssignments === 0} onClick={() => saveUserInteraction('sortCompletedAssignments', 0)}>Move to top</SettingsOption>
              <SettingsOption selected={sortCompletedAssignments === 1} onClick={() => saveUserInteraction('sortCompletedAssignments', 1)}>Move to bottom</SettingsOption>
            </SettingsOptions>
          </SettingsSection> */}
          <SettingsSection>
            <SettingsSectionHeader>Assignment View</SettingsSectionHeader>
            <SettingsOptions>
              <AutoWidthSettingsOption selected={showTimeInAssignment === 1} onClick={() => handleShowTimeInAssignment(1)}>
                <SettingsOptionHeader>Standard</SettingsOptionHeader>
                <Assignment {...assignmentPropsShim} />
              </AutoWidthSettingsOption>
              <AutoWidthSettingsOption selected={showTimeInAssignment === 0} onClick={() => handleShowTimeInAssignment(0)}>
                <SettingsOptionHeader>Compact</SettingsOptionHeader>
                <Assignment {...assignmentPropsShim} event={{...assignmentPropsShim.event, estimatedTime: 0}} showEstimatedTime={false} />
              </AutoWidthSettingsOption>
            </SettingsOptions>
          </SettingsSection>
          <SettingsSection>
            <SettingsSectionHeader>Completed Assignments</SettingsSectionHeader>
            <SettingsOptions>
              <AutoWidthSettingsOption selected={!hideCompletedAssignments} onClick={() => handleHideCompletedAssignments(false)}>
                <SettingsOptionHeader>Show</SettingsOptionHeader>
                <CalendarDayWithoutContext
                  {...assignmentPropsShim}
                  events={[{...assignmentPropsShim.event, complete: true}]}
                />
              </AutoWidthSettingsOption>
              <AutoWidthSettingsOption selected={hideCompletedAssignments} onClick={() => handleHideCompletedAssignments(true)}>
                <SettingsOptionHeader>Hide</SettingsOptionHeader>
                <CalendarDayWithoutContext
                  {...assignmentPropsShim}
                  events={[{...assignmentPropsShim.event, complete: true}]}
                  hideCompletedAssignments
                />
              </AutoWidthSettingsOption>
            </SettingsOptions>
          </SettingsSection>
          <SettingsSection>
            <SettingsSectionHeader>Study Speed</SettingsSectionHeader>
            <SettingsOptions mobileVertical>
              {[{scale: 1.5, label: 'No Rush'}, {scale: 1.25, label: 'Relaxed'}, {scale: 1, label: 'Default'}, {scale: .75, label: 'Faster'}, {scale: .5, label: 'Fastest'}].map(({scale, label}, index) => (
                <SettingsOption
                  style={{padding: '16px 13px', whiteSpace: 'nowrap'}}
                  key={label}
                  selected={studySpeed === scale}
                  onClick={() => saveUserInteraction('studySpeed', JSON.stringify(scale))}
                >
                  {label}
                </SettingsOption>
              ))}
            </SettingsOptions>
            <SettingsSectionSubheader style={{marginTop: 16}}>
              Gives you {studySpeed === 2 ? 'twice as much' : studySpeed === 1.5 ? '50% more' : studySpeed === 1.25 ? '25% more' : studySpeed === 1 ? 'the default' : studySpeed === .75 ? '25% less' : '50% less'} time to complete assignments. Does not affect custom assignments.
            </SettingsSectionSubheader>
          </SettingsSection>
          {shouldShowSubjectCoding && <SettingsSection>
            <SettingsSectionHeader>Assignment Color-Coding</SettingsSectionHeader>
            <SettingsOptions>
              <SettingsOption selected={useSubjectColors} onClick={() => handleUseSubjectColors(true)}>
                Subject
              </SettingsOption>
              <SettingsOption selected={!useSubjectColors} onClick={() => handleUseSubjectColors(false)}>
                Type
              </SettingsOption>
            </SettingsOptions>
            {!useSubjectColors && (
              <div style={{marginTop: 16}}>
                <SettingsSectionSubheader>
                  Assignments are color-coded by type:
                </SettingsSectionSubheader>
                <AssignmentTypeWrapper>
                  {assignmentTypeTitles
                    .map(([key, title], index) => (
                      <Chiclet
                        key={index}
                        color={typeDetails[key].color}>
                        {title}
                      </Chiclet>
                  ))}
                </AssignmentTypeWrapper>
              </div>
            )}
          </SettingsSection>}
          <SettingsSection>
            <SettingsSectionHeader>{bootcamp === 'dat' ? 'Dr. Ari’s 10-Week Study Schedule' : 'PDF Study Schedules'}</SettingsSectionHeader>
            <SettingsSectionSubheader>Looking for a PDF / printable alternative? {bootcamp === 'dat' ? 'Download Ari’s 10-week study schedule.' : 'Click the button below.'}</SettingsSectionSubheader>
            <StyledDownloadButton
              small
              href={bootcamp === 'inbde' ? 'https://docs.google.com/document/d/1JL5kU_tUSoDMU0Km6umldqIZ1RUvpLEUhdtYMrDuhdw/edit?usp=sharing' : bootcamp === 'dat' ? 'https://bootcamp.com/dat/aris-study-guide-for-dat-domination' : `https://bootcamp.com/${bootcamp}/study-schedule`}
              target={'_blank'}
              IconComponent={<DocumentIcon/>}
              iconPosition={'left'}
            >
              Download Study Schedule (PDF)
            </StyledDownloadButton>
          </SettingsSection>
          <SettingsSection>
            <SettingsSectionHeader>Reset Schedule</SettingsSectionHeader>
            <SettingsSectionSubheader>Reset your schedule to clear all completed assignments and generate a new schedule.</SettingsSectionSubheader>
            <ResetScheduleButton
              small
              onClick={() => setResetScreenVisible(true)}
              IconComponent={<ResetIcon/>}
              iconPosition={'left'}
              themePalette={{...theme.colors.interfacePalette.red}}>
                Reset Schedule
            </ResetScheduleButton>
          </SettingsSection>
        </Panel>
      </SettingsWrapper>
      <StyledButtonArea>
        <RoundButton full onClick={() => modalDispatch({type: 'close'})}>Save</RoundButton>
      </StyledButtonArea>
    </SettingsContainer>
  )
}

export default SettingsModal;